/**
 * Aspect — Environment & lighting pipeline
 * --------------------------------------------------------------
 * Configures the render pipeline per the engine rules: clustered lighting for
 * real-time lights, ACES tonemapping + sRGB, one shadow-casting "sun" plus a
 * cheap fill light, soft ambient, a photographic HDR sky (Poly Haven, CC0),
 * CameraFrame post-FX (SSAO, bloom, grading, colorEnhance, vignette), and
 * (via DayNightCycle) real-time sun/sky rotation through a 24-hour cycle.
 *
 * Energy conservation (rules): we rely on PlayCanvas's PBR (GGX) which already
 * enforces specular + diffuse <= incident; we simply avoid stacking unbounded
 * emissive/among lights.
 */

import * as pc from "playcanvas";

/** CC0 HDRI — kloofendal_48d_partly_cloudy_puresky (Poly Haven). */
export const SKY_HDRI_URL = "./sky/day_sky_1k.hdr";

/**
 * Device performance tier ("low" | "high"). Low tier scales down shadow
 * resolution, post-effects, and pixel ratio. Coarse pointers (phones/tablets)
 * and few-core CPUs land in "low"; ?quality=low|high forces a tier.
 */
export function detectPerfTier() {
  const forced = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("quality") : null;
  if (forced === "low" || forced === "high") return forced;
  const coarse = typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)")?.matches;
  const cores = navigator?.hardwareConcurrency ?? 8;
  return coarse || cores <= 4 ? "low" : "high";
}

/** Tune the clustered-lighting volume. Conservative cells keep mobile happy. */
export function setupRendering(app, { mobile = false, gfx = "desktop" } = {}) {
  const gl2 = app.graphicsDevice?.isWebGL2 !== false;
  if ("clusteredLightingEnabled" in app.scene) {
    app.scene.clusteredLightingEnabled = gl2;
  }
  const lighting = app.scene.lighting;
  const phoneHq = mobile && gfx !== "safe";
  if (lighting) {
    lighting.cells = mobile ? new pc.Vec3(8, 3, 8) : new pc.Vec3(10, 3, 10);
    lighting.maxLightsPerCell = mobile ? 16 : 32;
    lighting.shadowsEnabled = !mobile || gfx !== "safe";
    // Desktop: 4K atlas. Bedroom-only phones: 2K. Crash-loop: tiny atlas.
    lighting.shadowAtlasResolution = mobile ? (gfx === "hq" ? 2048 : gfx === "medium" ? 1024 : 512) : 4096;
    lighting.cookiesEnabled = false;
  }

  app.scene.toneMapping = pc.TONEMAP_ACES;
  app.scene.gammaCorrection = pc.GAMMA_SRGB;
  // Slightly lower flat ambient: room light pools + object shadows must read.
  app.scene.ambientLight = mobile && !phoneHq
    ? new pc.Color(0.58, 0.6, 0.64)
    : new pc.Color(0.4, 0.42, 0.46);
  if ("ambientLuminance" in app.scene) app.scene.ambientLuminance = 24000;
  app.scene.skyboxIntensity = 1.35;
  if ("skyboxHighlightMultiplier" in app.scene) app.scene.skyboxHighlightMultiplier = 1.75;
}

/** Sun (shadow caster) + a soft fill so shadowed faces are not pure black. */
export function addLights(app, { tier = detectPerfTier(), mobile = false, gfx = "desktop" } = {}) {
  const root = new pc.Entity("lighting");
  const phoneHq = mobile && gfx !== "safe";
  // PCF5 is the desktop look; PCF3 is the densest filter Safari can sustain.
  const shadowType = mobile
    ? (pc.SHADOW_PCF3 ?? pc.SHADOW_PCF3_32F ?? pc.SHADOW_PCF5)
    : pc.SHADOW_PCF5;
  const sunRes = mobile
    ? (gfx === "hq" ? 2048 : 1024)
    : (tier === "low" ? 1024 : 4096);

  const sun = new pc.Entity("sun");
  sun.addComponent("light", {
    type: "directional",
    color: new pc.Color(1.0, 0.96, 0.88),
    intensity: mobile ? (phoneHq ? 2.35 : 1.85) : 2.25,
    castShadows: !mobile || gfx !== "safe",
    shadowBias: mobile ? 0.04 : 0.05,
    normalOffsetBias: mobile ? 0.08 : 0.1,
    shadowDistance: mobile ? 32 : 40,
    shadowResolution: sunRes,
    shadowType,
    shadowIntensity: 1.0,
    affectDynamic: true,
    affectLightmapped: true,
  });
  sun.setLocalEulerAngles(52, 34, 0);
  root.addChild(sun);

  const fill = new pc.Entity("fill");
  fill.addComponent("light", {
    type: "directional",
    color: new pc.Color(0.68, 0.78, 0.95),
    intensity: mobile ? (phoneHq ? 0.42 : 0.55) : 0.55,
    castShadows: false,
  });
  fill.setLocalEulerAngles(40, 210, 0);
  root.addChild(fill);

  const top = new pc.Entity("top-fill");
  top.addComponent("light", {
    type: "directional",
    color: new pc.Color(0.88, 0.9, 0.98),
    intensity: mobile ? (phoneHq ? 0.32 : 0.45) : 0.42,
    castShadows: false,
  });
  top.setLocalEulerAngles(89, 0, 0);
  root.addChild(top);

  // Overhead spot: desktop always; bedroom-only phones get a 1K map so
  // furniture grounds on the floor. Crash-loop phones skip it.
  if (!mobile || gfx === "hq") {
    const ambientShadow = new pc.Entity("ambient-shadow");
    ambientShadow.addComponent("light", {
      type: "spot",
      color: new pc.Color(1.0, 1.0, 1.0),
      intensity: 0.5,
      castShadows: true,
      shadowBias: 0.2,
      normalOffsetBias: 0.35,
      range: 22,
      innerConeAngle: 82,
      outerConeAngle: 89.5,
      shadowResolution: mobile ? 1024 : (tier === "low" ? 2048 : 4096),
      shadowType,
      shadowIntensity: 0.85,
      affectDynamic: true,
      affectLightmapped: true,
    });
    ambientShadow.setLocalPosition(0, 2.5, 0);
    ambientShadow.setLocalEulerAngles(90, 0, 0);
    root.addChild(ambientShadow);
  }

  app.root.addChild(root);
  return root;
}

/**
 * Center the interior ambient-shadow spot under the active storey ceiling.
 */
export function placeAmbientShadowLight(lightingRoot, room) {
  const light = lightingRoot?.findByName("ambient-shadow");
  if (!light || !room) return;
  const b = room.bounds;
  const minX = b?.min?.[0] ?? 0;
  const maxX = b?.max?.[0] ?? 8;
  const minZ = b?.min?.[2] ?? 0;
  const maxZ = b?.max?.[2] ?? 8;
  const floorY = room.floorY ?? 0;
  const h = room.dimensions?.height ?? 2.8;
  const span = Math.max(maxX - minX, maxZ - minZ, 4);
  // Straight down from just under the ceiling slab.
  light.setPosition((minX + maxX) / 2, floorY + h - 0.3, (minZ + maxZ) / 2);
  light.setEulerAngles(90, 0, 0);
  if (light.light) {
    light.light.range = Math.max(16, span * 1.6 + h);
    light.light.outerConeAngle = 89.5;
    light.light.innerConeAngle = 82;
    light.light.intensity = 0.5;
    light.light.shadowIntensity = 0.85;
    light.light.shadowBias = 0.2;
    light.light.normalOffsetBias = 0.35;
    light.light.affectDynamic = true;
    light.light.affectLightmapped = true;
  }
}

/** Load an equirectangular .hdr (RGBE) from the public folder. */
function loadHdrEquirect(app, url) {
  return new Promise((resolve, reject) => {
    const asset = new pc.Asset("day-sky-hdri", "texture", { url }, {
      type: pc.TEXTURETYPE_RGBE,
      addressu: "clamp",
      addressv: "clamp",
    });
    asset.once("load", () => {
      const tex = asset.resource;
      tex.projection = pc.TEXTUREPROJECTION_EQUIRECT;
      resolve(tex);
    });
    asset.once("error", (err) => reject(err ?? new Error(`Failed to load sky HDRI: ${url}`)));
    app.assets.add(asset);
    app.assets.load(asset);
  });
}

/** Bake cubemap + env-atlas from an equirect HDR for sky rendering and PBR reflections. */
export function applyHdriEnvironment(app, equirect, { mobile = false, gfx = "desktop" } = {}) {
  try {
    const skybox = pc.EnvLighting.generateSkyboxCubemap(equirect);
    const phoneHq = mobile && gfx !== "safe";
    const lighting = pc.EnvLighting.generateLightingSource(equirect, {
      size: !mobile ? 256 : gfx === "hq" ? 256 : 64,
    });
    const envAtlas = pc.EnvLighting.generateAtlas(lighting, {
      size: !mobile ? 512 : gfx === "hq" ? 512 : 128,
      numReflectionSamples: !mobile ? 256 : gfx === "hq" ? 192 : 32,
      numAmbientSamples: !mobile ? 512 : gfx === "hq" ? 256 : 48,
    });
    lighting.destroy();

    app.scene.skyboxMip = mobile ? (phoneHq ? 1 : 2) : 0;
    app.scene.skyboxIntensity = 1.35;
    if ("skyboxLuminance" in app.scene) app.scene.skyboxLuminance = 24000;
    if ("skyboxHighlightMultiplier" in app.scene) app.scene.skyboxHighlightMultiplier = 1.75;
    app.scene.skybox = skybox;
    app.scene.envAtlas = envAtlas;

    // Rotate so the HDR sun sits near our key light direction.
    const rot = new pc.Quat();
    rot.setFromEulerAngles(0, -24, 0);
    if ("skyboxRotation" in app.scene) app.scene.skyboxRotation = rot;

    return { skybox, envAtlas };
  } catch (err) {
    console.warn("[aspect] HDR environment failed — continuing without sky atlas", err);
    return { skybox: null, envAtlas: null };
  }
}

/**
 * Load the photographic HDR sky and wire it into the scene.
 * @param {pc.AppBase} app
 * @param {{ url?: string }} [opts]
 */
export async function setupHdriSky(app, { url = SKY_HDRI_URL, mobile = false, gfx = "desktop" } = {}) {
  const equirect = await loadHdrEquirect(app, url);
  applyHdriEnvironment(app, equirect, { mobile, gfx });
  app.renderNextFrame = true;
  return { equirect, skyTexture: null };
}

/**
 * Post-processing stack via pc.CameraFrame.
 * Staging look: SSAO depth + punchy bloom + color grading (contrast / sat) +
 * HDR colorEnhance (vibrance / dehaze) + light vignette. Returns a controller
 * with quality presets and a leak-free destroy() (HMR rule).
 *
 * Quality tiers:
 *   high   — SSAO + TAA + full grade/bloom/vignette (desktop default)
 *   low    — bloom + grade + vignette only (desktop low-core)
 *   mobile — bloom + grade + vignette, no extra HDR buffers (Safari / phones)
 *   off    — plain forward rendering
 */
export function setupPostProcessing(app, camera, { quality } = {}) {
  let frame = null;
  let current = "off";

  const detect = detectPerfTier;

  const apply = (level) => {
    current = level;
    if (level === "off") {
      if (frame) {
        frame.destroy();
        frame = null;
      }
      app.renderNextFrame = true;
      return;
    }
    if (!frame) {
      try {
        frame = new pc.CameraFrame(app, camera.camera);
      } catch (err) {
        console.warn("[aspect] CameraFrame unavailable — forward shading", err);
        current = "off";
        app.renderNextFrame = true;
        return;
      }
    }
    // The frame's passes take over output transform; keep ACES + sRGB.
    frame.rendering.toneMapping = pc.TONEMAP_ACES;
    frame.rendering.samples = level === "high" ? 4 : 1;
    frame.rendering.sceneColorMap = false;

    if (level === "mobile") {
      // Max look Safari can hold: bloom + grade, no SSAO / TAA / fringing RTs.
      frame.bloom.intensity = 0.05;
      frame.bloom.blurLevel = 8;
      frame.vignette.inner = 0.62;
      frame.vignette.outer = 1.45;
      frame.vignette.curvature = 0.55;
      frame.vignette.intensity = 0.2;
      frame.grading.enabled = true;
      frame.grading.brightness = 1.05;
      frame.grading.contrast = 1.14;
      frame.grading.saturation = 1.18;
      frame.grading.tint = new pc.Color(1.02, 1.0, 0.98);
      frame.colorEnhance.enabled = false;
      frame.fringing.intensity = 0;
      frame.ssao.type = pc.SSAOTYPE_NONE;
      frame.taa.enabled = false;
      frame.update();
      app.renderNextFrame = true;
      return;
    }

    // Soft window / emissive glow — visible without washing interiors.
    frame.bloom.intensity = 0.055;
    frame.bloom.blurLevel = 10;

    // Gentle edge falloff — enough focus, not a muddy border.
    frame.vignette.inner = 0.62;
    frame.vignette.outer = 1.45;
    frame.vignette.curvature = 0.55;
    frame.vignette.intensity = 0.22;

    // Contrast + saturation pop (CameraFrame color grading).
    frame.grading.enabled = true;
    frame.grading.brightness = 1.05;
    frame.grading.contrast = 1.14;
    frame.grading.saturation = 1.2;
    frame.grading.tint = new pc.Color(1.02, 1.0, 0.98);

    // HDR-aware polish: lift shadows, tame highlights, smart vibrance.
    frame.colorEnhance.enabled = true;
    frame.colorEnhance.shadows = 0.18;
    frame.colorEnhance.highlights = -0.1;
    frame.colorEnhance.midtones = 0.06;
    frame.colorEnhance.vibrance = 0.32;
    frame.colorEnhance.dehaze = 0.14;

    // Tiny chromatic fringe on bright edges (cinematic, not psychedelic).
    frame.fringing.intensity = 3.5;

    if (level === "high") {
      frame.ssao.type = pc.SSAOTYPE_LIGHTING;
      frame.ssao.intensity = 0.9;
      frame.ssao.radius = 0.75;
      frame.ssao.samples = 16;
      frame.ssao.power = 2.4;
      frame.ssao.blurEnabled = true;

      frame.taa.enabled = true;
      frame.taa.jitter = 0.4;
    } else {
      frame.ssao.type = pc.SSAOTYPE_NONE;
      frame.taa.enabled = false;
    }
    frame.update();
    app.renderNextFrame = true;
  };

  apply(quality ?? detect());

  return {
    get quality() {
      return current;
    },
    /** Live pc.CameraFrame (null when quality is "off") — LUT grading hooks in here. */
    get frame() {
      return frame;
    },
    setQuality: apply,
    destroy() {
      if (frame) {
        frame.destroy();
        frame = null;
      }
    },
  };
}

/** A perspective camera with a comfortable default framing. */
export function createCamera(app, { mobile = false } = {}) {
  const camera = new pc.Entity("camera");
  camera.addComponent("camera", {
    clearColor: new pc.Color(0.72, 0.84, 0.94),
    fov: 60,
    nearClip: 0.1,
    farClip: mobile ? 80 : 200,
  });
  camera.setLocalPosition(0, 1.6, 6);
  camera.lookAt(0, 1, 0);
  app.root.addChild(camera);
  return camera;
}

/** Frame the camera to look across a loaded room from a back corner. */
export function frameCameraToRoom(camera, room) {
  if (!room?.bounds) return;
  const { min, max } = room.bounds;
  const cx = (min[0] + max[0]) / 2;
  const cz = (min[2] + max[2]) / 2;
  const depth = max[2] - min[2];
  const width = max[0] - min[0];
  camera.setLocalPosition(min[0] + width * 0.12, 1.7, min[2] + depth * 0.08);
  camera.lookAt(cx, 1.0, cz);
}
